/**
 * No Transactions Empty State
 * 
 * Displayed when user has no transactions yet.
 * Provides clear guidance on next steps with multiple entry points.
 * 
 * @see UX_ACTION_PLAN_ENHANCED_2025-10-13.md - Fix 5
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';

interface NoTransactionsEmptyStateProps {
  onUploadClick?: () => void;
  showDemoOption?: boolean;
}

export const NoTransactionsEmptyState = ({ 
  onUploadClick,
  showDemoOption = true 
}: NoTransactionsEmptyStateProps) => {
  const navigate = useNavigate();

  const handleUpload = () => {
    if (onUploadClick) {
      onUploadClick();
    } else {
      navigate('/file-processor');
    }
  };

  const handleDemo = () => {
    // TODO: Load demo data
    console.log('Load demo data');
  };

  const handleBankConnect = () => {
    // TODO: Implement bank connection
    navigate('/settings');
  };

  return (
    <Card className="border-2 border-dashed border-brand-border rounded-2xl">
      <CardHeader className="text-center pb-4">
        <div className="w-20 h-20 mx-auto mb-4 bg-brand-accent/20 rounded-2xl flex items-center justify-center">
          <i className="fa-solid fa-inbox text-4xl text-brand-primary"></i>
        </div>
        <CardTitle className="text-2xl font-space-grotesk text-brand-primary">
          No Transactions Yet
        </CardTitle>
        <p className="text-sm text-brand-text-secondary mt-2 max-w-md mx-auto">
          Get started by uploading your first bank export or connecting your bank account.
          Our AI will automatically classify 90% of your transactions.
        </p>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Primary Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-brand-accent/10 border-brand-accent hover:shadow-md transition-all cursor-pointer" onClick={handleUpload}>
            <CardContent className="pt-6 text-center">
              <div className="w-12 h-12 mx-auto mb-3 bg-brand-accent rounded-xl flex items-center justify-center">
                <i className="fa-solid fa-cloud-arrow-up text-2xl text-brand-primary"></i>
              </div>
              <h3 className="font-semibold text-brand-primary mb-2">Upload CSV File</h3>
              <p className="text-xs text-brand-text-secondary mb-4">
                Import bank statements, credit card exports
              </p>
              <Button size="sm" className="w-full">
                Upload Now
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-brand-secondary border-brand-border hover:shadow-md transition-all cursor-pointer" onClick={handleBankConnect}>
            <CardContent className="pt-6 text-center">
              <div className="w-12 h-12 mx-auto mb-3 bg-white border border-brand-border rounded-xl flex items-center justify-center">
                <i className="fa-solid fa-building-columns text-2xl text-brand-primary"></i>
              </div>
              <h3 className="font-semibold text-brand-primary mb-2">Connect Bank</h3>
              <p className="text-xs text-brand-text-secondary mb-4">
                Automatic sync with your bank account
              </p>
              <Button size="sm" variant="outline" className="w-full">
                Connect
                <Badge variant="secondary" className="ml-2 text-[10px]">Soon</Badge>
              </Button>
            </CardContent>
          </Card>

          {showDemoOption && (
            <Card className="bg-validation-low-bg border-validation-low hover:shadow-md transition-all cursor-pointer" onClick={handleDemo}>
              <CardContent className="pt-6 text-center">
                <div className="w-12 h-12 mx-auto mb-3 bg-white border border-validation-low rounded-xl flex items-center justify-center">
                  <i className="fa-solid fa-flask text-2xl text-brand-success"></i>
                </div>
                <h3 className="font-semibold text-brand-primary mb-2">Try Demo Data</h3>
                <p className="text-xs text-brand-text-secondary mb-4">
                  Explore with sample transactions
                </p>
                <Button size="sm" variant="outline" className="w-full">
                  Load Demo
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Pro Tips */}
        <Card className="bg-blue-50 border-blue-200 rounded-xl">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-start gap-3">
              <i className="fa-solid fa-lightbulb text-xl text-blue-600 flex-shrink-0"></i>
              <div>
                <h4 className="font-semibold text-brand-primary text-sm mb-2">Pro Tips</h4>
                <ul className="space-y-1 text-xs text-brand-text-secondary">
                  <li>✓ Supported formats: CSV, MT940, CAMT.053, Excel</li>
                  <li>✓ Typical upload takes less than 30 seconds</li>
                  <li>✓ AI will classify 90% automatically (8.1% VAT, accounts, etc.)</li>
                  <li>✓ You'll only need to review flagged items</li>
                  <li>✓ Swiss GAAP compliant by default</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Help Link */}
        <div className="text-center text-sm text-brand-text-secondary">
          Need help?{' '}
          <a href="#" className="text-brand-primary hover:underline font-medium">
            View documentation
          </a>
          {' '}or{' '}
          <a href="#" className="text-brand-primary hover:underline font-medium">
            watch tutorial video
          </a>
        </div>
      </CardContent>
    </Card>
  );
};
